import GlobalTable from "components/shared/table/GlobalTable";
import TraceWidget from "components/Trace/TraceWidget";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridColumns } from "pages/grid/hooks/useGridColumns";
import {
  joinBaseUrlAndPath,
  normalizeBareNumericEnvPath,
  replaceParams,
  replaceTrailingNumericWithEnvLang,
  resolveEnvNumericId,
  usesDecodeParamsEnvSegment,
} from "pages/grid/utils/gridHelpers";
import { canIDoThis } from "utils/helpers/rightsManager";
import { useAuth } from "utils/hooks/useAuth";
import { type DataSourceRecord } from "pages/widgets/components";
import {
  useGetDataSourcesQuery,
  useGetEnvironnementsQuery,
  useReadDatasourceQuery,
  useUpdateLayoutDefMutation,
} from "store/api";

type WidgetConfig = Record<string, any>;

type LayoutWidgetElement = {
  i: string;
  x?: number | string;
  y?: number | string;
  w?: number | string;
  h?: number | string;
  widget?: WidgetConfig | null;
};

type LayoutWidgetsProps = {
  items: LayoutWidgetElement[];
  layoutId?: string;
  rowActions?: Array<{
    key: string;
    label: string;
    icon?: string;
    ariaLabel?: string;
    danger?: boolean;
    onClick: (row: TableRecord) => void;
  }>;
};

type TableRecord = Record<string, any>;

const WIDGET_TYPE_TO_INPUT: Record<string, string> = {
  BasicWidget: "BasicWidgetInput",
  TableWidget: "TableWidgetInput",
  ObjectWidget: "ObjectWidgetInput",
  ChartWidget: "ChartWidgetInput",
  ColorCard: "ColorCardInput",
  TraceWidget: "TraceWidgetInput",
};

const GRID_COLUMNS = 12;
const GRID_GAP_PX = 16;
const GRID_ROW_HEIGHT_PX = 32;
const GRID_TRACK_HEIGHT_PX = GRID_ROW_HEIGHT_PX + GRID_GAP_PX;
type ResolvedGridItem = LayoutWidgetElement & { x: number; y: number; w: number; h: number };

const buildReadRequest = (dataSource: DataSourceRecord | undefined, envNumeric: number | undefined, lang: string) => {
  if (!dataSource?.read?.endpoint) return null;

  const rawEndpoint = String(dataSource.read.endpoint);
  const baseUrl = String(dataSource?.baseUrl ?? "");
  const useComposite =
    usesDecodeParamsEnvSegment(rawEndpoint) ||
    usesDecodeParamsEnvSegment(baseUrl) ||
    /##env##/.test(rawEndpoint);
  const envComposite = envNumeric != null && !Number.isNaN(envNumeric) ? `env=${envNumeric}&lang=${lang}` : null;

  const params: Record<string, string | number> = { lang };
  if (envNumeric != null && !Number.isNaN(envNumeric) && envComposite) {
    params.env = useComposite ? envComposite : String(envNumeric);
  }

  let endpoint = replaceParams(rawEndpoint, params);
  const hasEnvPlaceholder = /\{env\}|:env\b|##env##/.test(rawEndpoint);
  if (!hasEnvPlaceholder && endpoint.endsWith("/") && envNumeric != null && !Number.isNaN(envNumeric) && envComposite) {
    endpoint = `${endpoint}${useComposite ? envComposite : String(envNumeric)}`;
  }

  if (envNumeric != null && !Number.isNaN(envNumeric)) {
    endpoint = replaceTrailingNumericWithEnvLang(baseUrl, endpoint, envNumeric, lang);
    endpoint = normalizeBareNumericEnvPath(endpoint, envNumeric, lang);
  }

  endpoint = endpoint.replace(/\/+$/, "");

  return {
    url: joinBaseUrlAndPath(baseUrl, endpoint),
    method: String(dataSource?.read?.method ?? "GET"),
    datasourceId: Number(dataSource?.id ?? 0),
  };
};

const toSortedItems = (items: LayoutWidgetElement[]) => {
  return [...items].sort((left, right) => {
    const leftY = Number(left?.y ?? 0);
    const rightY = Number(right?.y ?? 0);
    if (leftY !== rightY) return leftY - rightY;

    const leftX = Number(left?.x ?? 0);
    const rightX = Number(right?.x ?? 0);
    return leftX - rightX;
  });
};

const clampNumber = (value: number, min: number, max: number) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const toGridBounds = (item: LayoutWidgetElement) => {
  const w = Math.min(GRID_COLUMNS, Math.max(2, Number(item.w ?? 6)));
  const h = Math.min(24, Math.max(2, Number(item.h ?? 2)));
  const x = clampNumber(Number(item.x ?? 0), 0, Math.max(0, GRID_COLUMNS - w));
  const y = Math.max(0, Number(item.y ?? 0));

  return { x, y, w, h };
};

const isOverlapping = (left: { x: number; y: number; w: number; h: number }, right: { x: number; y: number; w: number; h: number }) => {
  const separatedHorizontally = left.x + left.w <= right.x || right.x + right.w <= left.x;
  const separatedVertically = left.y + left.h <= right.y || right.y + right.h <= left.y;
  return !separatedHorizontally && !separatedVertically;
};

const resolveLayoutCollisions = (source: LayoutWidgetElement[], lockedId?: string) => {
  const normalizedById = new Map(
    source.map((entry): readonly [string, ResolvedGridItem] => {
      const bounds = toGridBounds(entry);
      return [
        entry.i,
        {
          ...entry,
          x: bounds.x,
          y: bounds.y,
          w: bounds.w,
          h: bounds.h,
        } as ResolvedGridItem,
      ] as const;
    })
  );

  const placed: ResolvedGridItem[] = [];

  if (lockedId) {
    const locked = normalizedById.get(lockedId);
    if (locked) {
      placed.push(locked);
      normalizedById.delete(lockedId);
    }
  }

  const remaining = toSortedItems(Array.from(normalizedById.values())) as ResolvedGridItem[];

  for (const entry of remaining) {
    const candidate: ResolvedGridItem = { ...entry };
    let guard = 0;

    while (placed.some((placedItem) => isOverlapping(candidate, placedItem))) {
      const blockers = placed.filter((placedItem) => isOverlapping(candidate, placedItem));
      const nextY = blockers.reduce((maxY, blocker) => Math.max(maxY, blocker.y + blocker.h), candidate.y);
      candidate.y = nextY;

      guard += 1;
      if (guard > 200) break;
    }

    placed.push(candidate);
  }

  const resolvedById = new Map(placed.map((entry) => [entry.i, entry]));
  return source.map((entry) => {
    const resolved = resolvedById.get(entry.i);
    return resolved ? { ...entry, x: resolved.x, y: resolved.y, w: resolved.w, h: resolved.h } : entry;
  });
};

const normalizeRows = (value: unknown): TableRecord[] => {
  let source: unknown[] = [];
  if (Array.isArray(value)) {
    source = value;
  } else {
    const nestedData = (value as { data?: unknown[] } | null)?.data;
    if (Array.isArray(nestedData)) {
      source = nestedData;
    }
  }

  return source.map((row: any, index: number) => ({
    key: row?.id ?? index + 1,
    ...row,
  }));
};

const BasicLayoutWidget: React.FC<{ widget: WidgetConfig }> = ({ widget }) => {
  return (
    <group data-direction="column" data-gap="10" data-wrap="no">
      {widget?.title ? <text data-weight="600">{String(widget.title)}</text> : null}
      <text data-wrap="wrap">{String(widget?.content ?? "")}</text>
    </group>
  );
};

const ColorCardLayoutWidget: React.FC<{ widget: WidgetConfig }> = ({ widget }) => {
  const backgroundByColor: Record<string, string> = {
    primary: "#2f80ed",
    secondary: "#6c757d",
    success: "#27ae60",
    danger: "#eb5757",
    warning: "#f2994a",
    info: "#56ccf2",
    light: "#f2f2f2",
    dark: "#333333",
  };

  const colorKey = String(widget?.color ?? "primary");
  const background = backgroundByColor[colorKey] ?? backgroundByColor.primary;
  const foreground = colorKey === "light" ? "#1f2937" : "#ffffff";

  return (
    <div
      style={{
        background,
        color: foreground,
        borderRadius: 12,
        padding: 16,
        minHeight: 120,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{String(widget?.title ?? "")}</div>
      <div style={{ whiteSpace: "pre-wrap" }}>{String(widget?.content ?? "")}</div>
    </div>
  );
};

const TableLayoutWidget: React.FC<{
  itemId: string;
  widget: WidgetConfig;
  rowActions?: Array<{
    key: string;
    label: string;
    icon?: string;
    ariaLabel?: string;
    danger?: boolean;
    onClick: (row: TableRecord) => void;
  }>;
}> = ({ itemId, widget, rowActions = [] }) => {
  const { data: dataSources = [] } = useGetDataSourcesQuery("");
  const { data: environnements = [] } = useGetEnvironnementsQuery("");

  const envLang = useMemo(() => {
    const storedEnvId = localStorage.getItem("_env_id");
    const storedEnvCode = localStorage.getItem("_env") || "";
    const lang = localStorage.getItem("_lang") || "fr";
    const envNumeric = resolveEnvNumericId(environnements, storedEnvId, storedEnvCode);
    return { lang, envNumeric };
  }, [environnements]);

  const selectedDataSource = useMemo(() => {
    return (Array.isArray(dataSources) ? dataSources : []).find(
      (entry: DataSourceRecord) => String(entry?.id ?? "") === String(widget?.table ?? "")
    ) as DataSourceRecord | undefined;
  }, [dataSources, widget?.table]);

  const request = useMemo(() => {
    const builtRequest = buildReadRequest(selectedDataSource, envLang.envNumeric, envLang.lang);
    if (!builtRequest) return null;

    return {
      ...builtRequest,
      layoutId: `layout-widget-${itemId}`,
    };
  }, [envLang.envNumeric, envLang.lang, itemId, selectedDataSource]);

  const { data, isLoading, isFetching } = useReadDatasourceQuery(request as any, {
    skip: !request,
    refetchOnMountOrArgChange: true,
  });

  const rows = useMemo(() => normalizeRows(data), [data]);
  const columns = useGridColumns({ tableWidget: widget, rows });

  return (
    <GlobalTable<TableRecord>
      rowKey="key"
      columns={columns}
      data={rows}
      tableTitle={String(widget?.title ?? widget?.name ?? "")}
      mode="client"
      searchable
      searchPlaceholder="Search"
      variant="legacy"
      rowActions={rowActions}
      loading={isLoading || isFetching}
      pagination={false}
    />
  );
};

const TraceLayoutWidget: React.FC<{ widget: WidgetConfig }> = ({ widget }) => {
  const { data: dataSources = [] } = useGetDataSourcesQuery("");

  const selectedDataSource = useMemo(() => {
    return (Array.isArray(dataSources) ? dataSources : []).find(
      (entry: DataSourceRecord) => String(entry?.id ?? "") === String(widget?.content ?? "")
    ) as DataSourceRecord | undefined;
  }, [dataSources, widget?.content]);

  return <TraceWidget fixedDataSource={selectedDataSource ?? null} />;
};

const UnsupportedLayoutWidget: React.FC<{ widget: WidgetConfig }> = ({ widget }) => {
  return (
    <group data-direction="column" data-gap="8" data-wrap="no">
      <text data-weight="600">{String(widget?.title ?? widget?.type ?? "Widget")}</text>
      <text data-color="neutral-600">{String(widget?.type ?? "Unknown widget")}</text>
    </group>
  );
};

const renderWidgetBody = (
  item: LayoutWidgetElement,
  rowActions?: Array<{
    key: string;
    label: string;
    icon?: string;
    ariaLabel?: string;
    danger?: boolean;
    onClick: (row: TableRecord) => void;
  }>
) => {
  const widget = item.widget ?? {};

  switch (widget.type) {
    case "BasicWidget":
      return <BasicLayoutWidget widget={widget} />;
    case "TableWidget":
      return <TableLayoutWidget itemId={item.i} widget={widget} rowActions={rowActions} />;
    case "TraceWidget":
      return <TraceLayoutWidget widget={widget} />;
    case "ColorCard":
      return <ColorCardLayoutWidget widget={widget} />;
    default:
      return <UnsupportedLayoutWidget widget={widget} />;
  }
};

const LayoutWidgets: React.FC<LayoutWidgetsProps> = ({ items, layoutId, rowActions = [] }) => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const [updateLayoutDef, { isLoading: isUpdatingLayout }] = useUpdateLayoutDefMutation();
  const canModifyLayout = canIDoThis({ role }, "layout_modify");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const layoutItemsRef = useRef<LayoutWidgetElement[]>(items);
  const resizeDraftRef = useRef<{ id: string; w: number; h: number } | null>(null);
  const resizeFrameRef = useRef<number | null>(null);
  const dragFrameRef = useRef<number | null>(null);
  const [layoutItems, setLayoutItems] = useState<LayoutWidgetElement[]>(items);
  const [resizingId, setResizingId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  useEffect(() => {
    setLayoutItems((current) => {
      const currentById = new Map(current.map((entry) => [entry.i, entry]));
      const merged = items.map((entry) => {
        const localEntry = currentById.get(entry.i);
        if (!localEntry) return entry;

        const localX = Number(localEntry.x ?? entry.x ?? 0);
        const localY = Number(localEntry.y ?? entry.y ?? 0);
        const localW = Number(localEntry.w ?? entry.w ?? 6);
        const localH = Number(localEntry.h ?? entry.h ?? 2);
        const incomingX = Number(entry.x ?? 0);
        const incomingY = Number(entry.y ?? 0);
        const incomingW = Number(entry.w ?? 6);
        const incomingH = Number(entry.h ?? 2);

        if (localX === incomingX && localY === incomingY && localW === incomingW && localH === incomingH) {
          return entry;
        }

        return {
          ...entry,
          x: localX,
          y: localY,
          w: localW,
          h: localH,
        };
      });

      const resolvedMerged = resolveLayoutCollisions(merged);
      layoutItemsRef.current = resolvedMerged;
      return resolvedMerged;
    });
  }, [items]);

  useLayoutEffect(() => {
    layoutItemsRef.current = layoutItems;
  }, [layoutItems]);

  const sortedItems = useMemo(() => toSortedItems(layoutItems), [layoutItems]);

  const persistLayoutItems = useCallback(
    async (nextLayout: LayoutWidgetElement[]) => {
      if (!layoutId || !canModifyLayout) return;

      await updateLayoutDef({
        id: layoutId,
        layout: nextLayout,
      }).unwrap();
    },
    [canModifyLayout, layoutId, updateLayoutDef]
  );

  const onEditWidget = (itemId: string, widgetType: unknown) => {
    if (!layoutId) return;
    if (typeof widgetType !== "string") return;
    const widgetTypeKey = WIDGET_TYPE_TO_INPUT[widgetType];
    if (!widgetTypeKey) return;
    navigate(`/layout/${layoutId}/widgets/${encodeURIComponent(widgetTypeKey)}?editWidgetId=${encodeURIComponent(itemId)}`);
  };

  const onRemoveWidget = async (itemId: string) => {
    if (!layoutId || !canModifyLayout) return;

    const nextLayout = layoutItems.filter((entry) => entry.i !== itemId);
    setLayoutItems(nextLayout);
    await persistLayoutItems(nextLayout);
  };

  const resizeLayoutItem = useCallback(
    (source: LayoutWidgetElement[], itemId: string, nextW: number, nextH: number) => {
      const resized = source.map((entry) =>
        entry.i === itemId
          ? {
              ...entry,
              w: nextW,
              h: nextH,
            }
          : entry
      );

      return resolveLayoutCollisions(resized, itemId);
    },
    []
  );

  const moveLayoutItem = useCallback(
    (source: LayoutWidgetElement[], itemId: string, nextX: number, nextY: number) => {
      let changed = false;

      const nextItems = source.map((entry) => {
        if (entry.i !== itemId) return entry;

        const prevX = Number(entry.x ?? 0);
        const prevY = Number(entry.y ?? 0);
        if (prevX === nextX && prevY === nextY) return entry;

        changed = true;
        return {
          ...entry,
          x: nextX,
          y: nextY,
        };
      });

      if (!changed) return source;
      return resolveLayoutCollisions(nextItems, itemId);
    },
    []
  );

  const onResizeStart = useCallback((event: React.MouseEvent<HTMLElement>, item: LayoutWidgetElement) => {
    if (!layoutId || !canModifyLayout || isUpdatingLayout) return;

    event.preventDefault();
    event.stopPropagation();

    const card = event.currentTarget.closest("[data-widget-card='true']") as HTMLDivElement | null;
    const container = containerRef.current;
    if (!card || !container) return;

    setResizingId(item.i);
    globalThis.document.body.style.userSelect = "none";
    globalThis.document.body.style.cursor = "nwse-resize";

    const startX = event.clientX;
    const startY = event.clientY;
    const startRect = card.getBoundingClientRect();
    const startWidthPx = startRect.width;
    const startHeightPx = startRect.height;
    const containerRect = container.getBoundingClientRect();
    const columnWidthPx = Math.max((containerRect.width - GRID_GAP_PX * (GRID_COLUMNS - 1)) / GRID_COLUMNS, 1);
    const widthStepPx = columnWidthPx + GRID_GAP_PX;
    const heightStepPx = GRID_TRACK_HEIGHT_PX;

    const minWidthPx = item.widget?.type === "BasicWidget" ? 220 : 360;
    const initialW = Number(item.w ?? 6);
    const initialH = Number(item.h ?? 2);
    const initialX = Math.max(0, Number(item.x ?? 0));
    const maxW = Math.max(2, GRID_COLUMNS - initialX);
    const minWByType = Math.min(maxW, Math.max(2, Math.ceil((minWidthPx + GRID_GAP_PX) / widthStepPx)));
    let hasActualResize = false;

    resizeDraftRef.current = {
      id: item.i,
      w: initialW,
      h: initialH,
    };

    const applyDraftDimensions = (widthPx: number, heightPx: number) => {
      resizeFrameRef.current != null && globalThis.cancelAnimationFrame(resizeFrameRef.current);

      resizeFrameRef.current = globalThis.requestAnimationFrame(() => {
        card.style.width = `${widthPx}px`;
        card.style.height = `${heightPx}px`;
        card.style.minHeight = `${heightPx}px`;
      });
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;

      const nextW = clampNumber(Math.round((startWidthPx + dx + GRID_GAP_PX) / widthStepPx), minWByType, maxW);
      const nextH = clampNumber(Math.round((startHeightPx + dy + GRID_GAP_PX) / heightStepPx), 2, 24);
      const nextWidthPx = nextW * columnWidthPx + (nextW - 1) * GRID_GAP_PX;
      const nextHeightPx = nextH * GRID_ROW_HEIGHT_PX + (nextH - 1) * GRID_GAP_PX;

      hasActualResize = hasActualResize || nextW !== initialW || nextH !== initialH;

      resizeDraftRef.current = { id: item.i, w: nextW, h: nextH };
      applyDraftDimensions(nextWidthPx, nextHeightPx);
    };

    const onMouseUp = async (upEvent: MouseEvent) => {
      upEvent.preventDefault();
      globalThis.removeEventListener("mousemove", onMouseMove);
      globalThis.removeEventListener("mouseup", onMouseUp);
      setResizingId(null);
      globalThis.document.body.style.userSelect = "";
      globalThis.document.body.style.cursor = "";

      resizeFrameRef.current != null && globalThis.cancelAnimationFrame(resizeFrameRef.current);
      resizeFrameRef.current = null;

      const draft = resizeDraftRef.current;
      if (!draft) return;

      if (!hasActualResize || (draft.w === initialW && draft.h === initialH)) {
        resizeDraftRef.current = null;
        card.style.width = "";
        card.style.height = "";
        card.style.minHeight = "";
        return;
      }

      const snapshot = resizeLayoutItem(layoutItemsRef.current, draft.id, draft.w, draft.h).map((entry) => ({ ...entry }));
      resizeDraftRef.current = null;

      layoutItemsRef.current = snapshot;
      setLayoutItems(snapshot);
      await persistLayoutItems(snapshot);

      card.style.width = "";
      card.style.height = "";
      card.style.minHeight = "";
    };

    globalThis.addEventListener("mousemove", onMouseMove);
    globalThis.addEventListener("mouseup", onMouseUp);
  }, [canModifyLayout, isUpdatingLayout, layoutId, persistLayoutItems, resizeLayoutItem]);

  const onHeaderDragStart = useCallback(
    (event: React.MouseEvent<HTMLElement>, item: LayoutWidgetElement) => {
      if (!layoutId || !canModifyLayout || isUpdatingLayout || resizingId) return;
      if (event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      const card = event.currentTarget.closest("[data-widget-card='true']") as HTMLDivElement | null;
      if (!card) return;

      const startX = event.clientX;
      const startY = event.clientY;
      const itemId = item.i;
      const itemW = Math.min(GRID_COLUMNS, Math.max(2, Number(item.w ?? 6)));
      const itemH = Math.min(24, Math.max(2, Number(item.h ?? 2)));
      let hasMoved = false;
      let dragLifted = false;
      let dragDx = 0;
      let dragDy = 0;

      setDraggingId(itemId);

      const applyDragTransform = () => {
        dragFrameRef.current = null;
        card.style.transform = `translate3d(${dragDx}px, ${dragDy}px, 0)`;
      };

      const scheduleTransform = () => {
        if (dragFrameRef.current != null) return;
        dragFrameRef.current = globalThis.requestAnimationFrame(applyDragTransform);
      };

      const onMouseMove = (moveEvent: MouseEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        if (!hasMoved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;

        hasMoved = true;

        if (!dragLifted) {
          dragLifted = true;
          card.style.transition = "none";
          card.style.willChange = "transform";
          card.style.zIndex = "20";
          card.style.boxShadow = "0 18px 40px rgba(15, 23, 42, 0.22)";
          globalThis.document.body.style.userSelect = "none";
          globalThis.document.body.style.cursor = "grabbing";
        }

        dragDx = dx;
        dragDy = dy;
        scheduleTransform();
      };

      const cleanupDragStyles = () => {
        dragFrameRef.current != null && globalThis.cancelAnimationFrame(dragFrameRef.current);
        dragFrameRef.current = null;
        card.style.transform = "";
        card.style.transition = "";
        card.style.willChange = "";
        card.style.zIndex = "";
        card.style.boxShadow = "";
        globalThis.document.body.style.userSelect = "";
        globalThis.document.body.style.cursor = "";
      };

      const onMouseUp = async (upEvent: MouseEvent) => {
        globalThis.removeEventListener("mousemove", onMouseMove);
        globalThis.removeEventListener("mouseup", onMouseUp);

        cleanupDragStyles();
        setDraggingId(null);

        if (!hasMoved) return;

        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return;

        const unitWidth = containerRect.width / GRID_COLUMNS;
        const unitHeight = GRID_TRACK_HEIGHT_PX;

        const rawX = Math.floor((upEvent.clientX - containerRect.left) / unitWidth) - Math.floor(itemW / 2);
        const rawY = Math.floor((upEvent.clientY - containerRect.top) / unitHeight) - Math.floor(itemH / 2);

        const nextX = clampNumber(rawX, 0, Math.max(0, GRID_COLUMNS - itemW));
        const nextY = clampNumber(rawY, 0, 240);

        const nextItems = moveLayoutItem(layoutItemsRef.current, itemId, nextX, nextY);
        if (nextItems === layoutItemsRef.current) return;

        layoutItemsRef.current = nextItems;
        setLayoutItems(nextItems);
        await persistLayoutItems(nextItems);
      };

      globalThis.addEventListener("mousemove", onMouseMove);
      globalThis.addEventListener("mouseup", onMouseUp);
    },
    [canModifyLayout, isUpdatingLayout, layoutId, moveLayoutItem, persistLayoutItems, resizingId]
  );

  return (
    <div
      ref={containerRef}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
        gridAutoRows: `${GRID_ROW_HEIGHT_PX}px`,
        alignItems: "stretch",
        gap: GRID_GAP_PX,
      }}
    >
      {sortedItems.map((item) => {
        const widget = item.widget ?? {};
        const widgetType = String(widget?.type ?? "");
        const itemX = Math.max(0, Number(item?.x ?? 0));
        const itemY = Math.max(0, Number(item?.y ?? 0));
        const itemW = Math.min(GRID_COLUMNS, Math.max(2, Number(item?.w ?? 6)));
        const itemH = Math.min(24, Math.max(2, Number(item?.h ?? 2)));

        let cardOpacity = 1;
        if (draggingId === item.i) {
          cardOpacity = 0.86;
        }

        let headerCursor = "default";
        if (canModifyLayout) {
          headerCursor = "grab";
          if (draggingId === item.i) {
            headerCursor = "grabbing";
          }
        }

        return (
          <div
            key={item.i}
            data-widget-card="true"
            data-widget-id={item.i}
            style={{
              gridColumn: `${itemX + 1} / span ${itemW}`,
              gridRow: `${itemY + 1} / span ${itemH}`,
              minWidth: widgetType === "BasicWidget" ? 220 : 360,
              background: "#ffffff",
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              overflow: "hidden",
              position: "relative",
              minHeight: Math.max(120, itemH * GRID_ROW_HEIGHT_PX),
              opacity: cardOpacity,
            }}
          >
            <div
              style={{
                padding: 16,
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                userSelect: "none",
              }}
            >
              <button
                type="button"
                aria-label="Drag widget by header"
                title="Drag widget"
                onMouseDown={(event) => onHeaderDragStart(event, item)}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "inherit",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: canModifyLayout ? headerCursor : "default",
                  textAlign: "left",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {canModifyLayout && <icon data-icon-size="small">drag_indicator</icon>}
                <span style={{ fontWeight: 600 }}>
                  {String(widget?.title ?? widget?.type ?? "Widget")}
                </span>
              </button>
              {canModifyLayout && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    aria-label="Edit widget"
                    aria-disabled={isUpdatingLayout}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      if (isUpdatingLayout) return;
                      event.preventDefault();
                      event.stopPropagation();
                      onEditWidget(item.i, widget?.type);
                    }}
                    style={{
                      border: "1px solid #d1d5db",
                      background: "#ffffff",
                      color: "#6b7280",
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: isUpdatingLayout ? "not-allowed" : "pointer",
                      lineHeight: 0,
                    }}
                    title="Edit widget"
                  >
                    <icon data-icon-size="small">edit</icon>
                  </button>
                  <button
                    type="button"
                    aria-label="Close widget"
                    aria-disabled={isUpdatingLayout}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      if (isUpdatingLayout) return;
                      event.preventDefault();
                      event.stopPropagation();
                      void onRemoveWidget(item.i);
                    }}
                    style={{
                      border: "1px solid #d1d5db",
                      background: "#ffffff",
                      color: "#6b7280",
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: isUpdatingLayout ? "not-allowed" : "pointer",
                      lineHeight: 0,
                    }}
                    title="Close widget"
                  >
                    <icon data-icon-size="small">close</icon>
                  </button>
                </div>
              )}
            </div>
            <div style={{ padding: 16 }}>
              {renderWidgetBody(item, rowActions)}
            </div>
            {canModifyLayout && (
              <button
                type="button"
                aria-label="Resize widget"
                title="Resize widget"
                onMouseDown={(event) => onResizeStart(event, item)}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                style={{
                  position: "absolute",
                  right: 4,
                  bottom: 4,
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  border: "1px solid #d1d5db",
                  color: "#6b7280",
                  background: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "nwse-resize",
                  opacity: resizingId === item.i ? 1 : 0.85,
                  userSelect: "none",
                }}
              >
                <icon data-icon-size="small">open_in_full</icon>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LayoutWidgets;